import { useRef, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import atoms from '@Components/Utilities/SandhillsAtoms';
import EmbeddedYouTubeVideoThumbnail from '@Components/Shared/EmbeddedYouTubeVideoThumbnail';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import RequestAQuote from '@Components/Home/RequestAQuote';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';

const RequestAQuoteView = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const requestAQuoteRef = useRef(null);
    const [expanded, setExpanded] = useState();
    const safeProps = {
        ...props,
        Industry: props.Industry || (props.Industries && props.Industries.length > 0 ? props.Industries[0].Value : ''),
        Category: props.Category || (props.Categories && props.Categories.length > 0 ? props.Categories[0].Value : ''),
    };

    const faqs = [
        { Question: 'Is it free to request equipment rental quotes on RentalYard?', Answer: 'Yes. You can submit a rental quote request through RentalYard to connect with equipment rental companies and dealers that may have equipment available for rent or lease.' },
        { Question: 'Are leasing options available?', Answer: 'Yes. Some providers on RentalYard also offer leasing options for users who need equipment for a longer period. When submitting your request, include details about how long you\'ll need the equipment so dealers and leasing companies can recommend the best available option.' },
        { Question: 'How fast will I receive a rental quote?', Answer: 'Response times vary by rental provider. However, in many cases, rental companies and dealers respond the same day, especially when the request includes clear details about the equipment needed and the rental timeframe.' },
        { Question: 'Can I compare quotes from multiple rental providers?', Answer: 'Yes. After rental providers share their quotes with you, you can review and compare them to find the option that works best for your needs. RentalYard does not set pricing or negotiate offers on behalf of providers or users, so you\'ll work directly with those providers to discuss pricing and rental terms.' },
        { Question: 'What types of equipment can I request quotes for?', Answer: 'Users can request quotes for construction equipment, farm machinery, turf and lawn care tools, trucks, trailers, attachments, UTVs, and other equipment listed on RentalYard.' },
        { Question: 'Do I need to know the exact equipment model I want to rent or lease?', Answer: 'No. If you don\'t know the specific make or model you need, you can describe the work to be completed, your jobsite conditions, and any attachments or performance requirements. Rental providers can use these details to recommend available equipment options that fit your needs.' },
        { Question: 'Will my quote include delivery and pickup costs?', Answer: 'Delivery and pickup options may vary by rental provider, location, equipment types, and other rental terms. If you need equipment delivered to a jobsite, include that information in your quote request so providers know in advance.' },
    ];

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const handleRequestAQuoteClick = () => {
        requestAQuoteRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (props.ScrollToRAQ) {
            requestAQuoteRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    return (
        <div className="request-a-quote-view-container">
            <div className="breadcrumb-container">
                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={props.BreadCrumbText} />
            </div>
            <div className="section">
                <div className="container">
                    <div className="flex-row" style={{ alignItems: "center" }}>
                        <div className="flex-col">
                            <h1>Request Equipment Rental Quotes From Local &amp; Nationwide Rental Providers</h1>
                            <h2>Compare Rental Equipment Pricing &amp; Availability Online</h2>
                            <p>
                                RentalYard helps contractors, farmers, fleet managers, homeowners, and other equipment users get equipment <a href="/dealer/directory/equipment-rental-companies/">rental quotes from dealers and rental companies in their area and across the country</a>. Submit a rental quote request, describe the equipment you need, and then compare responses based on pricing, availability, location, and rental terms.
                            </p>
                            <p>
                                Looking to rent <a href="/listings/for-rent/construction-equipment/4">heavy construction equipment</a>, <a href="/listings/for-rent/farm-equipment/464">farm machinery</a>, <a href="/listings/for-rent/trucks/16000">trucks</a>, <a href="/listings/for-rent/trailers/28">trailers</a>, or <a href="/listings/for-rent/attachments/all">attachments</a>? RentalYard makes it easier to find available equipment <a href="/dealer/directory/equipment-rental-companies/">from multiple providers</a> for construction jobs, farm projects, fleet needs, landscaping and tree service work, and other equipment-heavy projects in one place instead of searching provider by provider.
                            </p>
                            <div className="btn-container">
                                <button onClick={handleRequestAQuoteClick} className="ts-button quote-btn"><span>Request A Free Rental Quote</span></button>
                            </div>
                        </div>
                        <div className="flex-col col-4">
                            <img src={routes.CDNBaseUrl + 'Images/Info/RentalYard/RequestAQuote/quote-illustration.png'} alt="request a quote illustration" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="section grey-bg">
                <div className="container">
                    <h2>Get Free Rental Quotes Online</h2>
                    <p style={{ marginTop: "5px" }}>
                        Tired of scrolling through countless rental listings, placing endless calls, and making best-guess estimates on rental costs? With RentalYard, you can:
                    </p>
                    <ul className="bullet-list">
                        <li><div>Submit a free quote request in minutes</div></li>
                        <li><div>Receive equipment rental rates, availability details, and rental terms from multiple <a href="/dealer/directory/equipment-rental-companies/">professional dealers and rental companies</a> based on a single quote request</div></li>
                        <li><div>Compare dealer responses online to find the right rental option</div></li>
                        <li><div>Connect with equipment rental providers near you and across the country</div></li>
                    </ul>
                    <div className="btn-container">
                        <button onClick={handleRequestAQuoteClick} className="ts-button quote-btn"><span>Request A Free Rental Quote</span></button>
                    </div>
                </div>
            </div>
            <div className="section">
                <div className="container">
                    <h2>Built For Equipment Professionals</h2>
                    <p style={{ marginTop: "5px" }}>
                        RentalYard supports individuals and organizations across construction, agriculture, trucking, fleet management, logging, and other equipment-heavy industries who need to:
                    </p>
                    <ul className="bullet-list">
                        <li><div>Find competitive equipment rental rates from vetted providers</div></li>
                        <li><div>Source rental options for <a href="/listings/for-rent/construction-equipment/4">construction</a>, <a href="/listings/for-rent/forestry-equipment/1035">logging</a>, <a href="/listings/for-rent/farm-equipment/464">farming</a>, and other <a href="/listings/for-rent/equipment/all">equipment</a>-heavy projects</div></li>
                        <li><div>Compare equipment rental options from providers near you and across the country</div></li>
                        <li><div>Request equipment rentals quickly when deadlines are tight</div></li>
                    </ul>
                </div>
            </div>
            <div className="section grey-bg">
                <div className="container">
                    <h2>Request An Equipment Rental Quote In 3 Easy Steps</h2>
                    <p style={{ marginTop: "5px" }}>
                        RentalYard takes the work out of securing rental equipment. Watch how RentalYard quote requests work, then follow the steps below to submit your request and connect with rental providers.
                    </p>
                    <div className="flex-row" style={{ alignItems: "center", margin: "25px 0" }}>
                        <div className="flex-col">
                            <ul className='request-a-quote-instructions'>
                                <li>
                                    <div className="quote-icon">
                                        <i className='fa-regular fa-share-from-square'></i>
                                    </div>
                                    <div className="quote-text">
                                        <strong>1. Submit Your Rental Request</strong>
                                        <p>
                                            Tell us what equipment you need, where you need it, and when you need it. We’ll send your information to <a href="/dealer/directory/equipment-rental-companies/">rental providers and leasing companies</a> for review, depending on whether you need a short-term rental or a long-term lease.
                                        </p>
                                    </div>
                                </li>
                                <li>
                                    <div className="quote-icon">
                                        <i className='fa-solid fa-magnifying-glass'></i>
                                    </div>
                                    <div className="quote-text">
                                        <strong>2. Rental Companies And Dealers Review Your Inquiry</strong>
                                        <p>Upon receiving your request, they’ll be able to determine whether they have the right equipment available for your location, timeframe, and project needs.</p>
                                    </div>
                                </li>
                                <li>
                                    <div className="quote-icon">
                                        <i className='fa-regular fa-comments'></i>
                                    </div>
                                    <div className="quote-text">
                                        <strong>3. Compare And Communicate with Rental Companies</strong>
                                        <p>
                                            If there’s a match, equipment rental providers can respond with a personalized quote. From there, you can compare offers and work directly <a href="/dealer/directory/equipment-rental-companies/">with rental companies</a> to finalize pricing, availability, rental terms, and next steps to get your equipment faster.
                                        </p>
                                    </div>
                                </li>
                            </ul>
                            <div className="btn-container">
                                <button onClick={handleRequestAQuoteClick} className="ts-button quote-btn"><span>Request A Free Rental Quote</span></button>
                            </div>
                        </div>
                        <div className="flex-col">
                            <EmbeddedYouTubeVideoThumbnail
                                videoId="c2VyH5rHKl4"
                                altText="Watch how RentalYard quote requests work"
                                descriptionText="Watch how RentalYard quote requests work"
                                onOpen={() => gtmPushEvent(gtmEventType.youtube_thumbnail, 'videoName', "RequestAQuoteView_HowTo")}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="section">
                <div className="container">
                    <h2>What Information Should You Include In Your Rental Quote Request?</h2>
                    <p>
                        The more, the better! RentalYard quote requests include Manufacturer, Model, and Comments sections where you can share extra details about your project and equipment needs. The more details you provide, the easier it is for <a href="/dealer/directory/equipment-rental-companies/">rental companies</a> to recommend suitable equipment and provide accurate pricing information.
                    </p>
                    <p>
                        When submitting your rental quote request, use these sections to add details, such as equipment specifications, attachments, rental timeframe, pickup or delivery needs, and any other information that could help providers understand how and where you plan to use the equipment.
                    </p>
                    <p>
                        If you’re not sure of the exact type of machine or model you need, include details about the work you need to complete, your jobsite conditions, and any performance requirements. <a href="/dealer/directory/equipment-rental-companies/">Rental providers</a> can use this information to recommend available equipment options that fit your needs.
                    </p>
                </div>
            </div>
            <div className="section grey-bg">
                <div className="container">
                    <h2>What Affects Equipment Rental Pricing?</h2>
                    <p>
                        Equipment rental rates vary based on machine type, size, rental duration, location, demand, and a dealer’s or rental company’s individual rental terms. Even similar machines may have different rental costs based on their capabilities, operating requirements, and how in-demand they are.
                    </p>
                    <p>
                        For example, a <a href="/listings/for-rent/skid-steers/1055">skid steer</a> and a compact <a href="/listings/for-rent/wheel-loaders/1060">wheel loader</a> can both be compact, highly maneuverable machines that use buckets, forks, grapples, and many other attachments. However, their rental rates may differ because they are designed for different applications and may vary in lifting capacity, fuel use, operating costs, availability, and dealer demand.
                    </p>
                    <p>
                        Submitting a detailed rental request helps dealers and rental companies provide more accurate estimates for your project, which can make it easier to compare options and secure the right equipment rental for your work.
                    </p>
                </div>
            </div>
            <div className="section">
                <div className="container">
                    <h2>Rental Quote FAQ</h2>
                    <div className="accordion-container">
                        {faqs.map((faq, index) =>
                            <Accordion
                                key={index}
                                className={'accordion'}
                                disableGutters
                                expanded={expanded === 'panel' + index}
                                onChange={handleAccordionChange('panel' + index)}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon className={'expand-icon'} />}
                                    className={'accordion-header'}
                                    aria-controls={'panel' + index + '-content'}
                                >
                                    <strong>{faq.Question}</strong>
                                </AccordionSummary>
                                <AccordionDetails className={'accordion-content'}>
                                    {faq.Answer}
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </div>
                </div>
            </div>
            <div className="container grey-bg">
                <RequestAQuote ref={requestAQuoteRef} {...safeProps} />
            </div>
        </div>
    );
};

export default RequestAQuoteView;